// useStores.ts — shared hook for fetching tenant stores from the API.
// Used by the Topbar store switcher, master-data pages, and any component
// that needs the list of real stores for the current tenant.

import { useQuery } from '@tanstack/react-query';
import { apiFetch, apiFetchList } from './client';
import { registerStoreNames } from '@/domain/reference';
import { resolvePath, useRealApi } from './paths';
import { unwrapPage, storeDtoToStore } from './adapters';
import type { Store } from '@/domain/types';
import { STORES as REFERENCE_STORES, storeName as referenceStoreName } from '@/domain/reference';
import { useStore as useStoreContext } from '@/store/store';

export const storeKeys = {
  all: ['stores'] as const,
};

/**
 * Fetch the list of stores for the current tenant.
 * Returns the real API data when available, falls back to empty array.
 */
export function useStores() {
  const resolved = resolvePath('/stores');
  return useQuery({
    queryKey: storeKeys.all,
    queryFn: async (): Promise<Store[]> => {
      if (!resolved) return [];
      const data = await apiFetchList<unknown>(resolved.path);
      if (useRealApi) {
        const mapped = unwrapPage(data).map(storeDtoToStore);
        registerStoreNames(mapped);
        return mapped;
      }
      return (data as { id: string; name: string }[]).map((s) => ({
        ...s,
        code: s.id,
        active: true,
      }));
    },
    enabled: resolved !== null,
    staleTime: 5 * 60 * 1000, // stores rarely change — cache 5 min
  });
}

/** Stores for display/selection: the real tenant stores in real-API mode,
 *  the seeded reference list in mock mode. */
export function useDisplayStores(): Store[] {
  const { data: realStores = [] } = useStores();
  if (!useRealApi) return REFERENCE_STORES;
  return realStores;
}

/** Resolve a store id to its display name across both modes (uuid → real
 *  store name; mock id → reference name; unknown → the raw id). */
export function useStoreName(): (id: string) => string {
  const { data: realStores = [] } = useStores();
  return (id: string) => {
    const real = realStores.find((s) => s.id === id);
    if (real) return real.name;
    return referenceStoreName(id);
  };
}

/** The acting store id for writes: the topbar selection when specific, else
 *  the first store in scope. Returns empty string if no store is resolved yet
 *  (hooks should treat '' as "not ready"). */
export function useActiveStoreId(): string {
  const { store } = useStoreContext();
  const stores = useDisplayStores();
  if (store !== 'ALL') return store;
  if (stores.length > 0) return stores[0].id;
  // In real-API mode never fall back to a mock id; return '' to signal "loading"
  return useRealApi ? '' : 'ST-MAN';
}
